import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { geoEqualEarth, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import { COUNTRIES, ALL_CITIES } from "@/lib/activityData";
import { subscribeActivity } from "@/lib/activityBus";
import { getOnlineCount, subscribeOnlineCount } from "@/lib/onlineCount";

const W = 900;
const H = 440;
const TOPO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

export function MapDesLueurs() {
  const { t } = useTranslation();
  const [geos, setGeos] = useState(null);
  const [arcs, setArcs] = useState([]);
  const [pulses, setPulses] = useState([]);
  const lastCoords = useRef(null);
  const [count, setCount] = useState(getOnlineCount);

  useEffect(() => subscribeOnlineCount(setCount), []);

  const projection = useMemo(() => {
    const p = geoEqualEarth();
    p.fitExtent([[14, 14], [W - 14, H - 14]], { type: "Sphere" });
    return p;
  }, []);
  const pathGen = useMemo(() => geoPath(projection), [projection]);

  useEffect(() => {
    let alive = true;
    fetch(TOPO_URL)
      .then((r) => r.json())
      .then((topo) => {
        if (!alive) return;
        const fc = feature(topo, topo.objects.countries);
        setGeos(fc.features.map((f) => pathGen(f)).filter(Boolean));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [pathGen]);

  const cityPoints = useMemo(
    () => ALL_CITIES.map((c) => { const p = projection(c.coords); return p ? { x: p[0], y: p[1] } : null; }).filter(Boolean),
    [projection]
  );

  const arcPath = (a, b) => {
    const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
    const dist = Math.hypot(b[0] - a[0], b[1] - a[1]);
    const lift = Math.min(150, 36 + dist * 0.32);
    return `M ${a[0]} ${a[1]} Q ${mx} ${my - lift} ${b[0]} ${b[1]}`;
  };

  const fireArc = (fromCoords, toCoords, featured) => {
    const a = projection(fromCoords), b = projection(toCoords);
    if (!a || !b) return;
    const id = Math.random().toString(36).slice(2);
    setArcs((prev) => [...prev.slice(-6), { id, d: arcPath(a, b), featured }]);
    setPulses((prev) => [...prev.slice(-8), { id: id + "p", x: b[0], y: b[1], featured }]);
    setTimeout(() => setArcs((prev) => prev.filter((x) => x.id !== id)), featured ? 3200 : 2600);
    setTimeout(() => setPulses((prev) => prev.filter((x) => x.id !== id + "p")), 2000);
  };

  // Flux d'ambiance — ville à ville, en continu.
  useEffect(() => {
    const iv = setInterval(() => {
      if (document.hidden) return;
      const a = ALL_CITIES[Math.floor(Math.random() * ALL_CITIES.length)];
      const b = ALL_CITIES[Math.floor(Math.random() * ALL_CITIES.length)];
      if (a && b && a !== b) fireArc(a.coords, b.coords, false);
    }, 2600);
    return () => clearInterval(iv);
  }, [projection]);

  // Synchronisé avec la bannière : chaque événement relie la ville précédente à la nouvelle.
  useEffect(() => {
    const unsub = subscribeActivity((ev) => {
      const from = lastCoords.current || ev.coords;
      fireArc(from, ev.coords, true);
      lastCoords.current = ev.coords;
    });
    return unsub;
  }, [projection]);

  return (
    <section className="relative px-4 md:px-8 py-14 sm:py-20" data-testid="map-lueurs">
      <div className="max-w-6xl mx-auto text-center mb-8">
        <p className="text-primary/80 tracking-[0.28em] uppercase text-[11px] mb-3 inline-flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-70 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
          </span>
          {t("landing.mapLueurs.eyebrow")}
        </p>
        <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl leading-none pb-[0.05em]">{t("landing.mapLueurs.title")}</h2>
        <p className="mt-4 text-foreground/65 leading-relaxed max-w-2xl mx-auto">{t("landing.mapLueurs.subtitle")}</p>
      </div>

      <div className="max-w-5xl mx-auto relative rounded-[2rem] border border-primary/20 glass overflow-hidden p-3 sm:p-6"
        style={{ boxShadow: "0 30px 90px -50px rgba(0,0,0,0.9)" }}>
        <div aria-hidden className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(ellipse at 50% 40%, hsl(28 96% 56% / 0.10), transparent 66%)" }} />
        <svg viewBox={`0 0 ${W} ${H}`} className="relative w-full h-auto" data-testid="map-svg">
          <defs>
            <pattern id="lueur-dots" width="7" height="7" patternUnits="userSpaceOnUse">
              <circle cx="1.4" cy="1.4" r="1.1" fill="hsl(38 55% 58% / 0.40)" />
            </pattern>
            <linearGradient id="flux-grad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="hsl(38 95% 62% / 0)" />
              <stop offset="50%" stopColor="hsl(40 96% 64% / 0.95)" />
              <stop offset="100%" stopColor="hsl(30 90% 55% / 0.15)" />
            </linearGradient>
            <filter id="land-relief" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur in="SourceGraphic" stdDeviation="1.6" />
            </filter>
          </defs>

          {/* Relief : silhouette des terres (halo doux pour la profondeur) */}
          <g style={{ filter: "url(#land-relief)" }}>
            {geos && geos.map((d, i) => (
              <path key={i} d={d} fill="hsl(33 44% 15% / 0.72)" />
            ))}
          </g>

          {/* Continents : texture pointillée + frontières nettes */}
          <g>
            {geos && geos.map((d, i) => (
              <path key={i} d={d} fill="url(#lueur-dots)"
                stroke="hsl(37 72% 50% / 0.5)" strokeWidth="0.35"
                strokeLinejoin="round" strokeLinecap="round" />
            ))}
          </g>

          {/* Villes du réseau */}
          <g>
            {cityPoints.map((c, i) => (
              <circle key={i} cx={c.x} cy={c.y} r="1.5" fill="hsl(38 80% 60% / 0.5)" />
            ))}
          </g>

          {/* Arcs de flux */}
          <g fill="none">
            <AnimatePresence>
              {arcs.map((a) => (
                <motion.path
                  key={a.id}
                  d={a.d}
                  stroke="url(#flux-grad)"
                  strokeWidth={a.featured ? 2 : 1.1}
                  strokeLinecap="round"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: a.featured ? 1.3 : 1.0, ease: "easeInOut" }}
                  style={{ filter: a.featured ? "drop-shadow(0 0 5px hsl(40 96% 62% / 0.8))" : "none" }}
                />
              ))}
            </AnimatePresence>
          </g>

          {/* Pulsations d'arrivée */}
          <g fill="none">
            <AnimatePresence>
              {pulses.map((p) => (
                <motion.circle
                  key={p.id}
                  cx={p.x}
                  cy={p.y}
                  initial={{ r: 1.5, opacity: 0.85 }}
                  animate={{ r: p.featured ? 15 : 9, opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.8, ease: "easeOut" }}
                  stroke={p.featured ? "hsl(42 98% 66%)" : "hsl(38 85% 58%)"}
                  strokeWidth={p.featured ? 1.6 : 1}
                />
              ))}
            </AnimatePresence>
          </g>
          <g>
            <AnimatePresence>
              {pulses.map((p) => (
                <motion.circle
                  key={p.id + "d"}
                  cx={p.x}
                  cy={p.y}
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.6 }}
                  r={p.featured ? 3 : 2}
                  fill={p.featured ? "hsl(42 98% 72%)" : "hsl(38 90% 62%)"}
                />
              ))}
            </AnimatePresence>
          </g>
        </svg>

        <div className="relative mt-4 flex items-center justify-center gap-4 text-sm" data-testid="map-stats">
          <span className="inline-flex items-center gap-2 text-primary/90">
            <span className="font-display tabular-nums text-lg">{count}</span>
            <span className="text-foreground/60">{t("landing.mapLueurs.live")}</span>
          </span>
          <span className="text-foreground/30">·</span>
          <span className="text-foreground/70">
            <span className="font-display tabular-nums text-lg text-primary/90">{COUNTRIES.length}</span> {t("landing.mapLueurs.countries")}
          </span>
        </div>
      </div>
    </section>
  );
}
